import React, { useState } from "react";
import { Fan, Lightbulb, Radio, Bell, Power } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";

const IR_KEYS = [
  ["POWER", "Toggle system standby"],
  ["▲ / ▼", "Fan speed up / down"],
  ["FUNC", "Cycle fan AUTO mode"],
  ["◀ / ▶", "Enclosure light on / off"],
  ["EQ", "Toggle IR illuminator LED"],
  ["0–5", "Buzzer beep count"],
  ["ST/REPT", "Trigger camera snapshot"],
  ["U/SD", "Mute / unmute alerts"],
];

function Btn({ active, onClick, children, danger, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 rounded-md font-mono text-sm font-medium border transition-colors duration-200 active:scale-[0.98] ${
        active
          ? "bg-[var(--accent)] text-black border-[var(--accent)]"
          : danger
          ? "border-[var(--danger)]/40 text-[var(--danger)] hover:bg-[var(--danger)]/10"
          : "bg-[var(--surface2)] border-[var(--bd)] text-[var(--t1)] hover:border-[var(--bd-strong)]"
      }`}
    >
      {children}
    </button>
  );
}

function Group({ icon: Icon, title, children }) {
  return (
    <Panel className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-[var(--accent)]" />
        <Overline>{title}</Overline>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </Panel>
  );
}

export default function Controls() {
  const { sendCommand } = useApp();
  const [fan, setFan] = useState(null);
  const [light, setLight] = useState(false);
  const [ir, setIr] = useState(false);

  const fanOpts = [["OFF", "off"], ["30%", "30"], ["60%", "60"], ["100%", "100"], ["AUTO", "auto"]];

  return (
    <div className="space-y-6">
      <div>
        <Overline>Device Commands</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Remote Control</h2>
        <p className="text-sm text-[var(--t2)] mt-1">Commands publish to <span className="font-mono text-[var(--accent)]">campus/esp32/cmd</span> via HiveMQ.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Group icon={Fan} title="Fan Speed">
          {fanOpts.map(([label, val]) => (
            <Btn key={val} active={fan === val} testid={`fan-${val}`} onClick={() => { setFan(val); sendCommand("fan", val, `Fan ${label}`); }}>
              {label}
            </Btn>
          ))}
        </Group>

        <Group icon={Lightbulb} title="Enclosure Light">
          <Btn active={light} testid="light-on" onClick={() => { setLight(true); sendCommand("light", "on", "Light ON"); }}>ON</Btn>
          <Btn active={!light} testid="light-off" onClick={() => { setLight(false); sendCommand("light", "off", "Light OFF"); }}>OFF</Btn>
        </Group>

        <Group icon={Radio} title="IR Illuminator LED">
          <Btn active={ir} testid="ir-on" onClick={() => { setIr(true); sendCommand("ir", "on", "IR LED ON"); }}>ON</Btn>
          <Btn active={!ir} testid="ir-off" onClick={() => { setIr(false); sendCommand("ir", "off", "IR LED OFF"); }}>OFF</Btn>
        </Group>

        <Group icon={Bell} title="Buzzer — Rapid Beeps">
          {[1, 2, 3, 4, 5].map((n) => (
            <Btn key={n} testid={`buzzer-${n}`} onClick={() => sendCommand("buzzer", String(n), `Buzzer ×${n}`)}>{n}</Btn>
          ))}
        </Group>
      </div>

      {/* Emergency */}
      <Panel className="p-6">
        <Overline>Emergency Stop</Overline>
        <button
          data-testid="emergency-all-off-btn"
          onClick={() => { setFan("off"); setLight(false); setIr(false); sendCommand("system", "all_off", "ALL OFF — Emergency"); }}
          className="emergency-pulse mt-4 w-full py-6 rounded-lg bg-[var(--danger)] text-white font-heading font-black text-xl sm:text-2xl uppercase tracking-[0.25em] flex items-center justify-center gap-3 transition-transform active:scale-[0.99] hover:brightness-110"
        >
          <Power size={26} strokeWidth={3} /> ALL OFF
        </button>
        <p className="text-xs text-[var(--t2)] mt-2 font-mono text-center">Cuts fan, lights, IR LED and disarms outputs immediately.</p>
      </Panel>

      {/* IR Remote reference */}
      <Panel className="p-6">
        <Overline>IR Remote Reference</Overline>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="ir-reference-table">
            <thead>
              <tr className="text-left text-[var(--t2)] border-b border-[var(--bd)]">
                <th className="py-2 font-mono uppercase text-xs tracking-wider">Remote Key</th>
                <th className="py-2 font-mono uppercase text-xs tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody>
              {IR_KEYS.map(([k, a]) => (
                <tr key={k} className="border-b border-[var(--bd)] hover:bg-[var(--accent-soft)] transition-colors">
                  <td className="py-2.5 font-mono font-medium text-[var(--accent)]">{k}</td>
                  <td className="py-2.5 text-[var(--t2)]">{a}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
